import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  Search,
  X,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  Copy,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  PeriodGridPicker,
  getISOWeek,
  isoWeekToMonday,
} from '@/components/ui/PeriodGridPicker'
import { formatPower, formatEnergy } from '@/lib/utils'
import {
  useChargingSessions,
  useSystemChargingSessions,
  type TimeRange,
} from '@/hooks/use-monitoring'
import type { components } from '../../../../../types/flex-api'

type ChargingSession = components['schemas']['ChargingSession']
type SystemChargingSession = components['schemas']['SystemChargingSession']

interface ChargingSessionsViewProps {
  poolID?: string
  timeRange: TimeRange
  onTimeRangeChange: (r: TimeRange) => void
  systemId?: string | null
  onSystemIdChange?: (id: string | null) => void
  /** When set, locks the view to this system ID and hides the filter input. */
  lockedSystemId?: string
}

type SortDir = 'asc' | 'desc'

function formatDuration(startISO: string, endISO: string): string {
  const ms = new Date(endISO).getTime() - new Date(startISO).getTime()
  if (ms < 0) return '—'
  const totalMin = Math.floor(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}m`
  return `${h}h ${m}m`
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function TruncatedId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  const display = value.length > 12 ? value.slice(0, 6) + '…' + value.slice(-4) : value

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="font-mono text-xs hover:underline cursor-pointer"
            onClick={() => {
              navigator.clipboard.writeText(value)
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            }}
          >
            {display}
            {copied ? (
              <Check className="inline ml-1 h-3 w-3 text-green-500" />
            ) : (
              <Copy className="inline ml-1 h-3 w-3 opacity-40" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="font-mono text-xs">{value}</p>
          <p className="text-[10px] text-muted-foreground">Click to copy</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function SortHeader({
  label,
  sortKey,
  currentSort,
  currentDir,
  onSort,
}: {
  label: string
  sortKey: string
  currentSort: string
  currentDir: SortDir
  onSort: (key: string) => void
}) {
  const active = currentSort === sortKey
  return (
    <TableHead
      className="cursor-pointer select-none whitespace-nowrap hover:bg-muted/50 transition-colors"
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active &&
          (currentDir === 'asc' ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          ))}
      </span>
    </TableHead>
  )
}

type CalendarMode = 'month' | 'week' | 'day'

const PAGE_SIZE = 100

export default function ChargingSessionsView({
  poolID,
  timeRange,
  onTimeRangeChange,
  systemId,
  onSystemIdChange,
  lockedSystemId,
}: ChargingSessionsViewProps) {
  const [inputValue, setInputValue] = useState(systemId ?? '')
  const [sortKey, setSortKey] = useState('time')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [page, setPage] = useState(0)
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set())
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const effectiveSystemId = lockedSystemId ?? systemId ?? null

  useEffect(() => {
    setInputValue(systemId ?? '')
  }, [systemId])

  useEffect(() => {
    setPage(0)
  }, [timeRange, systemId, lockedSystemId, sortKey, sortDir])

  const handleInputChange = useCallback(
    (val: string) => {
      setInputValue(val)
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        onSystemIdChange?.(val.trim() || null)
      }, 400)
    },
    [onSystemIdChange]
  )

  const isFiltered = !!effectiveSystemId

  const poolQuery = useChargingSessions(poolID ?? '', timeRange, !isFiltered && !!poolID)
  const systemQuery = useSystemChargingSessions(effectiveSystemId ?? '', timeRange)

  const isLoading = isFiltered ? systemQuery.isLoading : poolQuery.isLoading
  const error = isFiltered ? systemQuery.error : poolQuery.error
  const refetch = isFiltered ? systemQuery.refetch : poolQuery.refetch

  const poolSessions = (poolQuery.data ?? []) as ChargingSession[]
  const systemSessions = (systemQuery.data ?? []) as SystemChargingSession[]

  // Sort
  const sortedPoolSessions = useMemo(() => {
    const sorted = [...poolSessions]
    const dir = sortDir === 'asc' ? 1 : -1
    sorted.sort((a, b) => {
      switch (sortKey) {
        case 'time':
          return dir * (new Date(a.pluggedAt).getTime() - new Date(b.pluggedAt).getTime())
        case 'duration':
          return (
            dir *
            (new Date(a.unpluggedAt).getTime() -
              new Date(a.pluggedAt).getTime() -
              (new Date(b.unpluggedAt).getTime() - new Date(b.pluggedAt).getTime()))
          )
        case 'startSoC':
          return dir * (a.pluggedSoC - b.pluggedSoC)
        case 'endSoC':
          return dir * (a.unpluggedSoC - b.unpluggedSoC)
        case 'desiredSoC':
          return dir * ((a.desiredSoC ?? 0) - (b.desiredSoC ?? 0))
        case 'departure':
          return (
            dir *
            (new Date(a.scheduledDepartureTime ?? 0).getTime() -
              new Date(b.scheduledDepartureTime ?? 0).getTime())
          )
        case 'ended':
          return dir * (new Date(a.unpluggedAt).getTime() - new Date(b.unpluggedAt).getTime())
        case 'maxPower':
          return dir * (a.maxChargePower - b.maxChargePower)
        case 'energy':
          return dir * (a.maxEnergy - b.maxEnergy)
        case 'mode':
          return dir * a.chargeMode.localeCompare(b.chargeMode)
        default:
          return 0
      }
    })
    return sorted
  }, [poolSessions, sortKey, sortDir])

  const sortedSystemSessions = useMemo(() => {
    const sorted = [...systemSessions]
    const dir = sortDir === 'asc' ? 1 : -1
    sorted.sort((a, b) => {
      switch (sortKey) {
        case 'time':
          return dir * (new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
        case 'duration':
          return (
            dir *
            (new Date(a.endedAt).getTime() -
              new Date(a.startedAt).getTime() -
              (new Date(b.endedAt).getTime() - new Date(b.startedAt).getTime()))
          )
        case 'ended':
          return dir * (new Date(a.endedAt).getTime() - new Date(b.endedAt).getTime())
        case 'energy':
          return dir * (a.totalChargedEnergyWh - b.totalChargedEnergyWh)
        case 'startSoC':
          return dir * (a.startSoC - b.startSoC)
        case 'endSoC':
          return dir * (a.endSoC - b.endSoC)
        case 'maxPower':
          return dir * (a.maxChargePowerW - b.maxChargePowerW)
        case 'phases':
          return dir * ((a.chargingPhases?.length ?? 0) - (b.chargingPhases?.length ?? 0))
        default:
          return 0
      }
    })
    return sorted
  }, [systemSessions, sortKey, sortDir])

  const totalPoolPages = Math.max(1, Math.ceil(sortedPoolSessions.length / PAGE_SIZE))
  const paginatedPoolSessions = useMemo(
    () => sortedPoolSessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedPoolSessions, page]
  )

  const totalSystemPages = Math.max(1, Math.ceil(sortedSystemSessions.length / PAGE_SIZE))
  const paginatedSystemSessions = useMemo(
    () => sortedSystemSessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedSystemSessions, page]
  )

  function toggleExpanded(sessionID: string) {
    setExpandedSessions((prev) => {
      const next = new Set(prev)
      if (next.has(sessionID)) next.delete(sessionID)
      else next.add(sessionID)
      return next
    })
  }

  function handleSort(key: string) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  // Time range toolbar state
  const isRolling = typeof timeRange === 'string'
  const calendarMode: CalendarMode =
    typeof timeRange === 'object' && timeRange.type === 'week'
      ? 'week'
      : typeof timeRange === 'object' && timeRange.type === 'day'
        ? 'day'
        : 'month'

  const now = new Date()
  const defaultMonth =
    now.getMonth() === 0
      ? `${now.getFullYear() - 1}-12`
      : `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0')}`
  const dayOfWeek = (now.getDay() + 6) % 7
  const thisWeekMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek)
  const lastWeekMonday = new Date(thisWeekMonday.getTime() - 7 * 86400000)
  const defaultWeek = getISOWeek(lastWeekMonday)

  function switchToCalendar(mode: CalendarMode) {
    if (mode === 'month') {
      onTimeRangeChange({ type: 'month', month: defaultMonth })
    } else if (mode === 'week') {
      onTimeRangeChange({ type: 'week', isoWeek: defaultWeek })
    } else {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      const y = yesterday.getFullYear()
      const m = String(yesterday.getMonth() + 1).padStart(2, '0')
      const d = String(yesterday.getDate()).padStart(2, '0')
      onTimeRangeChange({ type: 'day', date: `${y}-${m}-${d}` })
    }
  }

  function stepCalendar(dir: -1 | 1) {
    if (calendarMode === 'day') {
      const date = (timeRange as { type: 'day'; date: string }).date
      const d = new Date(date)
      d.setDate(d.getDate() + dir)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      onTimeRangeChange({ type: 'day', date: `${y}-${m}-${dd}` })
    } else if (calendarMode === 'week') {
      const isoWeek = (timeRange as { type: 'week'; isoWeek: string }).isoWeek
      const monday = isoWeekToMonday(isoWeek)
      monday.setDate(monday.getDate() + dir * 7)
      onTimeRangeChange({ type: 'week', isoWeek: getISOWeek(monday) })
    } else {
      const month = (timeRange as { type: 'month'; month: string }).month
      const [y, m] = month.split('-').map(Number)
      let newY = y
      let newM = m + dir
      if (newM > 12) {
        newM = 1
        newY++
      }
      if (newM < 1) {
        newM = 12
        newY--
      }
      onTimeRangeChange({ type: 'month', month: `${newY}-${String(newM).padStart(2, '0')}` })
    }
  }

  function isAtLatestPeriod(): boolean {
    if (calendarMode === 'day') {
      const date = (timeRange as { type: 'day'; date: string }).date
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      return date >= todayStr
    }
    if (calendarMode === 'week') {
      const isoWeek = (timeRange as { type: 'week'; isoWeek: string }).isoWeek
      return isoWeek >= getISOWeek(new Date())
    }
    const month = (timeRange as { type: 'month'; month: string }).month
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return month >= currentMonth
  }

  const sessionCount = isFiltered ? sortedSystemSessions.length : sortedPoolSessions.length

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2 shrink-0 flex-wrap">
        {/* System ID filter — hidden when locked to a specific system */}
        {!lockedSystemId && (
          <>
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Filter by System ID…"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                className="pl-8 pr-8 h-8 text-xs"
              />
              {inputValue && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setInputValue('')
                    onSystemIdChange?.(null)
                    clearTimeout(debounceRef.current)
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {isFiltered && (
              <Badge variant="secondary" className="text-[10px]">
                System
              </Badge>
            )}
          </>
        )}

        <div className="flex-1" />

        {/* Rolling presets */}
        <div className="flex gap-1">
          {(['24h', '7d', '30d'] as const).map((r) => (
            <Button
              key={r}
              variant={isRolling && timeRange === r ? 'default' : 'outline'}
              size="sm"
              onClick={() => onTimeRangeChange(r)}
              disabled={isLoading}
            >
              {r}
            </Button>
          ))}
        </div>

        <div className="w-px h-5 bg-border" />

        {/* Calendar mode toggle */}
        <div className="flex gap-1">
          {(['day', 'week', 'month'] as const).map((mode) => (
            <Button
              key={mode}
              variant={!isRolling && calendarMode === mode ? 'default' : 'outline'}
              size="sm"
              onClick={() => switchToCalendar(mode)}
              disabled={isLoading}
            >
              {mode === 'month' ? 'Month' : mode === 'week' ? 'Week' : 'Day'}
            </Button>
          ))}
        </div>

        {!isRolling && (
          <div className="flex items-center gap-1">
            <button
              className="h-7 w-6 flex items-center justify-center rounded border hover:bg-accent transition-colors disabled:opacity-30"
              onClick={() => stepCalendar(-1)}
              disabled={isLoading}
              aria-label="Previous period"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            {calendarMode !== 'day' ? (
              <PeriodGridPicker
                mode={calendarMode}
                value={
                  calendarMode === 'month'
                    ? (timeRange as { type: 'month'; month: string }).month
                    : (timeRange as { type: 'week'; isoWeek: string }).isoWeek
                }
                onChange={(v) => {
                  if (calendarMode === 'month') {
                    onTimeRangeChange({ type: 'month', month: v })
                  } else {
                    onTimeRangeChange({ type: 'week', isoWeek: v })
                  }
                }}
                disabled={isLoading}
              />
            ) : (
              <Button variant="outline" size="sm" disabled={isLoading}>
                {(timeRange as { type: 'day'; date: string }).date}
              </Button>
            )}
            <button
              className="h-7 w-6 flex items-center justify-center rounded border hover:bg-accent transition-colors disabled:opacity-30"
              onClick={() => stepCalendar(1)}
              disabled={isLoading || isAtLatestPeriod()}
              aria-label="Next period"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Count + pagination info */}
      <div className="px-4 pb-2 shrink-0 flex items-center gap-3">
        <span className="text-xs text-muted-foreground">
          {isLoading
            ? 'Loading…'
            : sessionCount > PAGE_SIZE
              ? `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, sessionCount)} of ${sessionCount} sessions`
              : `${sessionCount} session${sessionCount !== 1 ? 's' : ''}`}
        </span>
        {((!isFiltered && totalPoolPages > 1) || (isFiltered && totalSystemPages > 1)) && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="text-xs tabular-nums text-muted-foreground px-1">
              {page + 1}/{isFiltered ? totalSystemPages : totalPoolPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              disabled={page >= (isFiltered ? totalSystemPages : totalPoolPages) - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 pb-2 shrink-0">
          <p className="text-xs text-destructive">{(error as Error).message}</p>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto px-4 pb-4">
        {!isFiltered ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Session ID</TableHead>
                <SortHeader label="Started" sortKey="time" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Ended" sortKey="ended" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Duration" sortKey="duration" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Start SoC" sortKey="startSoC" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="End SoC" sortKey="endSoC" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Desired SoC" sortKey="desiredSoC" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Departure" sortKey="departure" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Mode" sortKey="mode" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Max Power" sortKey="maxPower" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Energy" sortKey="energy" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPoolSessions.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-muted-foreground py-12">
                    No charging sessions found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPoolSessions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell><TruncatedId value={s.id} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{formatDateTime(s.pluggedAt)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{formatDateTime(s.unpluggedAt)}</TableCell>
                    <TableCell className="text-xs tabular-nums">{formatDuration(s.pluggedAt, s.unpluggedAt)}</TableCell>
                    <TableCell className="text-xs tabular-nums">{s.pluggedSoC}%</TableCell>
                    <TableCell className="text-xs tabular-nums">{s.unpluggedSoC}%</TableCell>
                    <TableCell className="text-xs tabular-nums">{s.desiredSoC != null ? `${s.desiredSoC}%` : '—'}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{s.scheduledDepartureTime ? formatDateTime(s.scheduledDepartureTime) : '—'}</TableCell>
                    <TableCell>
                      <Badge variant={s.chargeMode === 'DEPARTURE_TIME_EV' ? 'default' : 'secondary'} className="text-[10px]">
                        {s.chargeMode === 'DEPARTURE_TIME_EV' ? 'Scheduled' : 'Forced'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums">{formatPower(s.maxChargePower)}</TableCell>
                    <TableCell className="text-xs tabular-nums">{formatEnergy(s.maxEnergy)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Session ID</TableHead>
                <TableHead className="whitespace-nowrap">Asset ID</TableHead>
                <SortHeader label="Started" sortKey="time" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Ended" sortKey="ended" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Duration" sortKey="duration" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Energy" sortKey="energy" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Start SoC" sortKey="startSoC" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="End SoC" sortKey="endSoC" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Max Power" sortKey="maxPower" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Phases" sortKey="phases" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedSystemSessions.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground py-12">
                    No charging sessions found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSystemSessions.flatMap((s) => {
                  const phases = s.chargingPhases ?? []
                  const isExpanded = expandedSessions.has(s.sessionID)
                  const rows = [
                    <TableRow
                      key={s.sessionID}
                      className={phases.length > 0 ? 'cursor-pointer hover:bg-muted/50' : ''}
                      onClick={() => phases.length > 0 && toggleExpanded(s.sessionID)}
                    >
                      <TableCell>
                        <span className="inline-flex items-center gap-1">
                          {phases.length > 0 && (
                            <ChevronDown
                              className={`h-3 w-3 shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                            />
                          )}
                          <TruncatedId value={s.sessionID} />
                        </span>
                      </TableCell>
                      <TableCell><TruncatedId value={s.assetID} /></TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{formatDateTime(s.startedAt)}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{formatDateTime(s.endedAt)}</TableCell>
                      <TableCell className="text-xs tabular-nums">{formatDuration(s.startedAt, s.endedAt)}</TableCell>
                      <TableCell className="text-xs tabular-nums">{formatEnergy(s.totalChargedEnergyWh)}</TableCell>
                      <TableCell className="text-xs tabular-nums">{s.startSoC}%</TableCell>
                      <TableCell className="text-xs tabular-nums">{s.endSoC}%</TableCell>
                      <TableCell className="text-xs tabular-nums">{formatPower(s.maxChargePowerW)}</TableCell>
                      <TableCell className="text-xs tabular-nums">{phases.length}</TableCell>
                    </TableRow>,
                  ]
                  if (isExpanded && phases.length > 0) {
                    rows.push(
                      <TableRow key={`${s.sessionID}-phases`} className="bg-muted/30">
                        <TableCell colSpan={10} className="p-0">
                          <div className="px-8 py-2">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                              Charging Phases
                            </p>
                            <Table>
                              <TableHeader>
                                <TableRow className="text-[10px]">
                                  <TableHead className="h-7 whitespace-nowrap">#</TableHead>
                                  <TableHead className="h-7 whitespace-nowrap">Started</TableHead>
                                  <TableHead className="h-7 whitespace-nowrap">Ended</TableHead>
                                  <TableHead className="h-7 whitespace-nowrap">Duration</TableHead>
                                  <TableHead className="h-7 whitespace-nowrap">Mode</TableHead>
                                  <TableHead className="h-7 whitespace-nowrap">Energy</TableHead>
                                  <TableHead className="h-7 whitespace-nowrap">Start SoC</TableHead>
                                  <TableHead className="h-7 whitespace-nowrap">End SoC</TableHead>
                                  <TableHead className="h-7 whitespace-nowrap">Median Power</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {phases.map((p, i) => (
                                  <TableRow key={i} className="text-[11px]">
                                    <TableCell className="py-1 tabular-nums">{i + 1}</TableCell>
                                    <TableCell className="py-1 whitespace-nowrap">{formatDateTime(p.startedAt)}</TableCell>
                                    <TableCell className="py-1 whitespace-nowrap">{formatDateTime(p.endedAt)}</TableCell>
                                    <TableCell className="py-1 tabular-nums">{formatDuration(p.startedAt, p.endedAt)}</TableCell>
                                    <TableCell className="py-1">
                                      <Badge variant={p.chargeMode === 'DEPARTURE_TIME_EV' ? 'default' : 'secondary'} className="text-[9px]">
                                        {p.chargeMode === 'DEPARTURE_TIME_EV' ? 'Scheduled' : 'Forced'}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="py-1 tabular-nums">{formatEnergy(p.energyWh)}</TableCell>
                                    <TableCell className="py-1 tabular-nums">{p.startSoC}%</TableCell>
                                    <TableCell className="py-1 tabular-nums">{p.endSoC}%</TableCell>
                                    <TableCell className="py-1 tabular-nums">{formatPower(p.medianChargePowerW)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  }
                  return rows
                })
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
